import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
  try {
    const u = JSON.parse(localStorage.getItem("userElwnsh") || "{}");
    if (u?.token) return u.token;
  } catch (_) {
    /* ignore */
  }
  return localStorage.getItem("token");
};

// Fetch all winch partner documents (optional query filters)
export const fetchWinchPartnerDocuments = createAsyncThunk(
  "winch_partner_documents/fetchAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const queryParams = new URLSearchParams();
      const add = (key, val) => {
        if (val === undefined || val === null) return;
        if (typeof val === "string" && val.trim() === "") return;
        queryParams.append(key, String(val).trim());
      };

      add("document_number", filters.document_number);
      add("winch_partner_id", filters.winch_partner_id);
      add("document_type", filters.document_type);
      add("auto_renewal", filters.auto_renewal);
      add("status", filters.status);
      add("date_filter", filters.date_filter);
      if (filters.date_filter === "range") {
        add("from_date", filters.from_date);
        add("to_date", filters.to_date);
      }
      if (filters.quick_filter) {
        add("quick_filter", filters.quick_filter);
      }

      const qs = queryParams.toString();
      const url = `${BASE_URL}/partner-documents${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Add winch partner document
export const addWinchPartnerDocument = createAsyncThunk(
  "winch_partner_documents/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all form fields
      if (formData.winch_partner_id) data.append("winch_partner_id", formData.winch_partner_id);
      if (formData.document_type) data.append("document_type", formData.document_type);
      if (formData.document_date) data.append("document_date", formData.document_date);
      if (formData.expiry_date) data.append("expiry_date", formData.expiry_date);
      if (formData.expiry_date_check) data.append("expiry_date_check", formData.expiry_date_check);
      if (formData.auto_renewal !== undefined && formData.auto_renewal !== null && formData.auto_renewal !== "") {
        data.append("auto_renewal", formData.auto_renewal);
      }
      if (formData.status) data.append("status", formData.status);
      if (formData.reminder_enabled !== undefined && formData.reminder_enabled !== null && formData.reminder_enabled !== "") {
        data.append("reminder_enabled", formData.reminder_enabled);
      }
      if (formData.file) {
        data.append("file", formData.file);
      }

      const response = await axios.post(`${BASE_URL}/partner-documents`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add document";
      return rejectWithValue(message);
    }
  }
);

// Update document status
export const updateDocumentStatus = createAsyncThunk(
  "winch_partner_documents/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(`${BASE_URL}/partner-documents/${id}/status`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return { id, status, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update document status";
      return rejectWithValue(message);
    }
  }
);

const WinchPartnerDocumentsSlice = createSlice({
  name: "winch_partner_documents",
  initialState: {
    documents: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch documents
      .addCase(fetchWinchPartnerDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        const raw = action.payload?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        state.documents = list;
      })
      .addCase(fetchWinchPartnerDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Add document
      .addCase(addWinchPartnerDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addWinchPartnerDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Document added successfully";
      })
      .addCase(addWinchPartnerDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add document";
      })
      // Update document status
      .addCase(updateDocumentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateDocumentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.data?.message || "Status updated successfully";
        // Update the document status in the list
        const documentIndex = state.documents.findIndex(
          (doc) => doc.id === action.payload.id
        );
        if (documentIndex !== -1) {
          state.documents[documentIndex].status = action.payload.status;
        }
      })
      .addCase(updateDocumentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update document status";
      });
  },
});

export const { clearState } = WinchPartnerDocumentsSlice.actions;
export default WinchPartnerDocumentsSlice.reducer;
